import React, { useState } from 'react';

import { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useSearchParams } from 'react-router-dom';

import {
  useFreshShipmentCacheInvalidation,
  useShipmentGroupMastersQuery,
  useUpdateFertTransaction,
} from '@/services/fresh-shipment/hooks';
import { TOA_GetFreshShipmentSplitFert } from '@/types.gql';

import { BILL_OPTIONS } from '../utils/constants';

import './FreshShipmentSplitDialog.css';

type Props = {
  params: ICellRendererParams;
};

export const FreshShipmentSplitDialog = ({ params }: Props) => {
  const [urlParams] = useSearchParams();
  const siteId = urlParams.get('sId');
  const shipGroupId = urlParams.get('group-id');
  const fertilizationId = params.data?.fertilizationId;

  const invalidateCache = useFreshShipmentCacheInvalidation();

  const [dialogVisible, setDialogVisible] = useState(false);
  const [formData, setFormData] =
    useState<TOA_GetFreshShipmentSplitFert | null>(null);

  const { mutateAsync: updateFertTransaction } = useUpdateFertTransaction();

  const { data: incubatorData } = useShipmentGroupMastersQuery({
    fertilizationSiteId: siteId,
    shipGroupId: shipGroupId,
  });
  const incubatorList = incubatorData?.shipmentGroupMasters;

  const handleSplit = () => {
    setFormData({
      fertilizationId: params.data?.fertilizationId,
      shipmentGroupMaster2Id: params.data?.shipmentGroupMaster2Id ?? '',
      shipmentGroupMaster2Name: params.data?.shipmentGroupMaster2Name ?? '',
      shipped2: params.data?.shipped2 ?? null,
      shippedTog2: params.data?.shippedTog2 ?? null,
      billIncubator2: params.data?.billIncubator2 ?? null,
      billshipping2: params.data?.billShipping2 ?? null,
    } as TOA_GetFreshShipmentSplitFert);
    setDialogVisible(true);
  };

  const handleRefreshData = () => {
    invalidateCache(['FertilizationListFreshShipment']);
  };

  const handleClose = () => {
    handleRefreshData();
    setDialogVisible(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const key = name as keyof TOA_GetFreshShipmentSplitFert;

    const updatedFormData = {
      ...formData!,
      [key]: value,
    };
    setFormData(updatedFormData);
    updateFertTransaction({
      id: fertilizationId,
      field: key,
      value: value,
      updatePredictedComments: false,
    });
  };

  const handleDropdownChange = (e: DropdownChangeEvent) => {
    const { name, value } = e.target;
    const key = name as keyof TOA_GetFreshShipmentSplitFert;

    const updatedFormData = {
      ...formData!,
      [key]: value === 'true' ? true : value === 'false' ? false : value,
    };
    setFormData(updatedFormData);
    updateFertTransaction({
      id: fertilizationId,
      field: key,
      value: value,
      updatePredictedComments: false,
    });
  };

  return (
    <div className="fresh-shipment-split-button__wrapper">
      <Button
        onClick={handleSplit}
        className="fresh-shipment-split-button"
        severity="secondary"
      >
        Split
      </Button>
      <Dialog
        header="Split Shipment"
        visible={dialogVisible}
        onHide={handleClose}
      >
        {formData && (
          <div className="fresh-shipment-split-dialog__fields">
            <div className="fresh-shipment-split-dialog__field">
              <label htmlFor="shipmentGroupMaster2Id">
                Ship Group Master 2:
              </label>
              <Dropdown
                id="shipmentGroupMaster2Id"
                name="shipmentGroupMaster2Id"
                value={formData.shipmentGroupMaster2Id}
                options={incubatorList || []}
                onChange={handleDropdownChange}
                optionLabel="name"
                optionValue="shipmentGroupMasterId"
                placeholder="Select Ship Group Master 2"
              />
            </div>

            <div className="fresh-shipment-split-dialog__field">
              <label htmlFor="shipped2">Ship Client 2:</label>
              <InputText
                id="shipped2"
                name="shipped2"
                value={formData.shipped2?.toString() ?? ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="fresh-shipment-split-dialog__field">
              <label htmlFor="shippedTog2">Ship TOG 2:</label>
              <InputText
                id="shippedTog2"
                name="shippedTog2"
                value={formData.shippedTog2?.toString() ?? ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="fresh-shipment-split-dialog__field">
              <label htmlFor="billIncubator2">Bill Inc 2:</label>
              <Dropdown
                id="billIncubator2"
                name="billIncubator2"
                value={formData.billIncubator2 ? 'true' : 'false'}
                options={BILL_OPTIONS}
                onChange={handleDropdownChange}
                optionLabel="text"
                optionValue="value"
                placeholder="Select Bill Inc 2"
              />
            </div>

            <div className="fresh-shipment-split-dialog__field">
              <label htmlFor="billshipping2">Bill Ship 2:</label>
              <Dropdown
                id="billshipping2"
                name="billshipping2"
                value={formData.billshipping2 ? 'true' : 'false'}
                options={BILL_OPTIONS}
                onChange={handleDropdownChange}
                optionLabel="text"
                optionValue="value"
                placeholder="Select Bill Ship 2"
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};
